import { BodyContentType } from "@/domain/entities/emails/Email";
import useHorizontalScrollbar from "@/hooks/mails/useHorizontalScrollbar";
import {
    useIframeBridge,
    useIframeSrcDoc,
} from "@/hooks/mails/useIframeBridge";
import { cn } from "@/lib/utils";
import { FC, useId } from "react";

// Ignore sub-pixel overflow so the bar doesn't flap on/off at the boundary.
const OVERFLOW_TOLERANCE = 2;

type MailIframeProps = {
    content: string;
    contentType: BodyContentType;
    className?: string;
};

const MailIframe: FC<MailIframeProps> = ({
    content,
    contentType = BodyContentType.HTML,
    className,
}) => {
    const { iframeRef, contentWidth, containerWidth, scrollLeft, scrollTo } =
        useIframeBridge();
    const {
        trackRef,
        thumb,
        isDragging,
        handleTrackClick,
        handleThumbMouseDown,
        handleKeyDown,
    } = useHorizontalScrollbar(
        contentWidth,
        containerWidth,
        scrollLeft,
        scrollTo,
    );

    const srcdoc = useIframeSrcDoc(content, contentType);
    const iframeId = useId();
    const hasOverflow =
        containerWidth > 0 &&
        contentWidth > containerWidth + OVERFLOW_TOLERANCE;

    return (
        <div className={cn("relative", className)}>
            {/* Captures pointer events over the iframe while dragging, so the gesture isn't stolen by it. */}
            {isDragging && <div className="absolute inset-0 z-20" />}
            <iframe
                ref={iframeRef}
                className="w-full overflow-hidden border-0"
                id={iframeId}
                referrerPolicy="no-referrer"
                sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
                srcDoc={srcdoc}
                title="Email content"
            />
            {hasOverflow && (
                // h-0 keeps the bar out of the layout flow so it can't feed back into the iframe height (resize loop).
                <div className="pointer-events-none sticky bottom-0 z-10 h-0">
                    <div
                        ref={trackRef}
                        aria-controls={iframeId}
                        aria-orientation="horizontal"
                        aria-valuemax={thumb.scrollableRange}
                        aria-valuemin={0}
                        aria-valuenow={Math.round(scrollLeft)}
                        className="pointer-events-auto absolute inset-x-0 bottom-0 h-4 rounded-b-md border-t border-neutral-lighter bg-neutral-lightest"
                        onClick={handleTrackClick}
                        onKeyDown={handleKeyDown}
                        role="scrollbar"
                        tabIndex={0}
                    >
                        <div
                            className="absolute top-1/2 h-1.5 -translate-y-1/2 rounded-full bg-black/[0.28] transition-colors duration-150 hover:bg-black/40"
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={handleThumbMouseDown}
                            style={{ width: thumb.width, left: thumb.left }}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default MailIframe;
